'use client';
import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuthStore } from 'store/useAuthStore';

// Define route to module mapping. Permissions are handled at root-module level only.
const ROUTE_MODULE_MAP: Record<string, string> = {
  '/event-analytics/overview': 'analytics',
  '/event-analytics/pages-and-screens': 'analytics',
  '/event-analytics/events': 'analytics',
  '/event-analytics': 'analytics',
  '/user': 'user',
  '/project-details/projects': 'project_details',
  '/project-details/clusters': 'project_details',
  '/project-details/towers': 'project_details',
  '/project-details/units': 'project_details',
  '/project-details': 'project_details',
  '/project-updates': 'project_updates',
  '/content': 'content',
  '/help-and-support': 'help_and_support',
  '/admin': 'admin',
};

// Routes that don't require authentication
const PUBLIC_ROUTES = ['/', '/sign-in', '/unauthorised'];

interface PermissionGuardProps {
  children: React.ReactNode;
}

function checkAccess(user: any, module: string): boolean {
  return user.permission_list.some((p: any) => p.module === module);
}

export function PermissionGuard({ children }: PermissionGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user } = useAuthStore();
  const [ready, setReady] = useState(false);

  const EXCLUDED_PATHS = ['/icons', '/images', '/favicon.ico'];

  const getStoredToken = () => {
    if (typeof window === 'undefined') return null;
    try {
      const auth = useAuthStore.getState();
      if (!auth.token) return null;
      return auth.token;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    // Skip check for public routes and static assets
    if (
      PUBLIC_ROUTES.includes(pathname) ||
      pathname.startsWith('/_next') ||
      pathname.startsWith('/api') ||
      EXCLUDED_PATHS.some((prefix) => pathname.startsWith(prefix))
    ) {
      setReady(true);
      return;
    }

    const storedToken = getStoredToken();

    if (!user) {
      if (storedToken) {
        // Wait for persisted auth store hydration
        return;
      }
      router.push('/sign-in');
      return;
    }

    const requiredModule = getRequiredModule(pathname);

    if (requiredModule) {
      const hasAccess = checkAccess(user, requiredModule);
      if (!hasAccess) {
        router.push('/unauthorised');
        return;
      }
    }

    setReady(true);
  }, [pathname, user, router]);

  if (!ready) {
    return null;
  }

  return <>{children}</>;
}

function getRequiredModule(pathname: string): string | null {
  // Check exact matches first
  if (ROUTE_MODULE_MAP[pathname]) {
    return ROUTE_MODULE_MAP[pathname];
  }

  // Check prefix matches
  for (const [route, module] of Object.entries(ROUTE_MODULE_MAP)) {
    if (pathname.startsWith(route + '/')) {
      return module;
    }
  }

  return null;
}